import axios from 'axios';
import type { SupplierProduct } from '../types/supplier-product-types';
import { createCancelablePromise } from '../../../../services/promises/create-cancelable-promise';

type SupplierProductGridFilter = {
  productName?: string;
};

type SupplierProductGridRequest = {
  filter?: SupplierProductGridFilter;
  page?: {
    pageNumber: number;
    pageSize: number;
  };
};

type SupplierProductGridResponse = {
  products: SupplierProduct[];
  totalCount: number;
};

export const fetchSupplierProductsGrid = (supplierId: number, filterRequest: SupplierProductGridRequest = {}) => {
  const requestPromise = axios
    .post<SupplierProductGridResponse>(`/supplier/${supplierId}/products/grid`, filterRequest)
    .then((response) => ({
      products: response.data.products ?? [],
      totalCount: response.data.totalCount ?? 0,
    }));

  return createCancelablePromise(requestPromise);
};

type CreateSupplierProductRequest = {
  productId: number;
  supplierId: number;
  supplierSku: string;
  cost: number;
};

export const createSupplierProduct = async (data: CreateSupplierProductRequest): Promise<SupplierProduct> => {
  const response = await axios.post('/product/supplier', data);
  return response.data;
};

type UpdateSupplierProductRequest = {
  supplierSku: string;
  cost: number;
};

export const updateSupplierProduct = async (productSupplierGuid: string, data: UpdateSupplierProductRequest): Promise<SupplierProduct> => {
  const response = await axios.put(`/product/supplier/${productSupplierGuid}`, data);
  return response.data;
};

export const deleteSupplierProduct = async (productSupplierGuid: string): Promise<void> => {
  await axios.delete(`/product/supplier/${productSupplierGuid}`);
};

type BulkCreateSupplierProductRequest = {
  supplierId: number;
  products: Array<{
    productId: number;
    supplierSku: string;
    cost: number;
    productUoMId?: number;
  }>;
};

type FailureReason = {
  productId: number;
  errorCode: string;
  reason: string;
};

export type BulkCreateSupplierProductsResponse = {
  successCount: number;
  failedCount: number;
  failures?: FailureReason[];
  status: number;
};

export const bulkCreateSupplierProducts = async (data: BulkCreateSupplierProductRequest): Promise<BulkCreateSupplierProductsResponse> => {
  const response = await axios.post('/product/supplier/bulk', data);
  return {
    ...response.data,
    status: response.status,
  };
};

type BulkUpdateSupplierProductRequest = {
  products: Array<{
    productSupplierGuid: string;
    supplierSku: string;
    cost: number;
    productUoMId?: number;
  }>;
};

type BulkUpdateFailureReason = {
  productSupplierGuid: string;
  errorCode: number;
  reason: string;
};

export type BulkUpdateSupplierProductsResponse = {
  successCount: number;
  failedCount: number;
  failures: BulkUpdateFailureReason[];
};

export const bulkUpdateSupplierProducts = async (data: BulkUpdateSupplierProductRequest): Promise<BulkUpdateSupplierProductsResponse> => {
  const response = await axios.post('/product/supplier/bulkUpdate', data);
  return response.data;
};

type SupplierProductsData = {
  supplierId: number;
  productIds: number[];
};

export type SupplierProductData = {
  productSupplierGuid: string | null;
  supplierSku: string;
  cost: number;
  productUoMId: number | null;
};

export type SupplierProductsDataResponse = Record<string, SupplierProductData[]>;

export const fetchSupplierProductsData = async (data: SupplierProductsData): Promise<SupplierProductsDataResponse> => {
  const response = await axios.post<SupplierProductsDataResponse>(`/product/supplier/data`, data);
  return response.data;
};
